import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/vue-query"

import { commonGetNextPageParam } from "@finq/app-base/utils/common/fetcher-utils"

import { PensionCategoriesEnum } from "../types/portfolio"

export function useMyPortfolioInfo() {
  const { pensionPortfolioService, additionalParams } = usePensionPortfolioService()

  return useQuery<MyPortfolioInfo>({
    queryKey: ["PensionPortfolioService.getPortfolioInfo", additionalParams],
    queryFn: ({ signal }) => pensionPortfolioService.value.getPortfolioInfo({ signal }),
    staleTime: Infinity,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export type GetMyPortfolioPoliciesReturnType = Awaited<
  ReturnType<typeof PensionPortfolioService.getMyPortfolioPolicies>
>
export function useMyPensionPortfolioPolicies(
  selectedCategory: Ref<PensionCategoriesEnum | undefined>,
  options: InfiniteQueryOptions<GetMyPortfolioPoliciesReturnType> = {}
) {
  const { pensionPortfolioService, additionalParams } = usePensionPortfolioService()

  return useInfiniteQuery({
    queryKey: ["PensionPortfolioService.getMyPortfolioPolicies", selectedCategory, additionalParams],
    queryFn: ({ signal }) => {
      return pensionPortfolioService.value.getMyPortfolioPolicies({
        signal,
        params: jsonClean({ category: selectedCategory?.value }),
      })
    },
    staleTime: Infinity,
    initialPageParam: 0,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    getNextPageParam: commonGetNextPageParam,
    retry: false,
    ...options,
  })
}

export function useMyPensionDeposits(policyId: Ref<number>) {
  const { pensionPortfolioService, additionalParams } = usePensionPortfolioService()

  return useQuery({
    queryKey: ["PensionPortfolioService.getMyDeposits", policyId, additionalParams],
    queryFn: ({ signal }) => pensionPortfolioService.value.getMyDeposits(policyId.value, { signal }),
    staleTime: Infinity,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export function useMyPensionExclusions() {
  const { pensionPortfolioService, additionalParams } = usePensionPortfolioService()

  const query = useQuery({
    queryKey: ["PensionPortfolioService.getUserExclusions", additionalParams],
    queryFn: ({ signal }) => pensionPortfolioService.value.getUserExclusions({ signal }),
    staleTime: Infinity,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  })

  const userHasExclusions = computed(() => Boolean(query.data?.value?.length))

  return {
    ...query,
    userHasExclusions,
  }
}

export function useMyValuePropositionCalculation() {
  const { pensionPortfolioService, additionalParams } = usePensionPortfolioService()

  return useQuery({
    queryKey: ["PensionsService.getValuePropositionCalculation", additionalParams],
    // queryFn: ({ signal }) => pensionPortfolioService.value.getValuePropositionCalculation({ signal }),
    queryFn: async ({ signal }) => {
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return {
        data: {
          once: {
            items: [
              {
                id: 1,
                productCategoryId: "EDUCATION",
                currentSavings: 210793,
                forecastWithoutFinq: 605525,
                forecastWithFinq: 605525,
                difference: 0,
              },
              {
                id: 2,
                productCategoryId: "PROVIDENT",
                currentSavings: 10881,
                forecastWithoutFinq: 46820,
                forecastWithFinq: 46820,
                difference: 0,
              },
              {
                id: 3,
                productCategoryId: "MANAGERS",
                currentSavings: 50856,
                forecastWithoutFinq: 112095,
                forecastWithFinq: 114771,
                difference: 2676,
              },
            ],
            summary: {
              currentSavings: 242896,
              forecastWithoutFinq: 1705038,
              forecastWithFinq: 1744214,
              difference: 39176,
            },
          },

          // Mock data for monthly allowance
          monthlyAllowance: {
            items: [
              {
                id: 1,
                productCategoryId: "COMPREHENSIVE",
                currentSavings: 409041,
                forecastWithoutFinq: 6491,
                forecastWithFinq: 7511,
                difference: 1020,
              },
              {
                id: 2,
                productCategoryId: "MANAGERS",
                currentSavings: 72981,
                forecastWithoutFinq: 814,
                forecastWithFinq: 994,
                difference: 180,
              },
            ],
            summary: {
              currentSavings: 482022,
              forecastWithoutFinq: 7305,
              forecastWithFinq: 8505,
              difference: 1200,
            },
          },
        },
      }
    },
    staleTime: Infinity,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

const PoliciesMockData = [
  {
    type: PensionCategoriesEnum.EDUCATION,
    data: [
      {
        productCategoryId: "EDUCATION",
        policyNumberId: "10734582",
        companyId: "511880460",
        fundId: "963",
        fundShortText: "אנליסט השתלמות מניות",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/analyst.png",
        companyShortText: "אנליסט",
        joinDate: "2021-09-29T21:00:00.000Z",
        liquidityDate: "2027-09-29T21:00:00.000Z",
        currentSavings: 69127,
        employerName: "אקשן אייטם סולושנס  בעמ",
        futureRetirementWealthWithDeposits: 0,
        futureRetirementWealthWithoutDeposits: 0,
        status: "ACTIVE",
        posStand: "EMPLOYEE",
        depositManagementFees: null,
        accManagementFees: "0.8",
        employeeProvision: 2.5,
        employerProvision: 7.5,
        compensationProvision: null,
        salary: 0,
        employeeDeposits: 3535.2,
        employerDeposits: 1178.4,
        totalYearlyDeposits: 4713.6,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: null,
        currentWealth: 69127,
        futureAllowanceWithoutDeposit: null,
        futureAllowanceWithDeposit: null,
        monthlyFutureAllowanceWithoutDeposit: null,
        monthlyFutureAllowanceWithDeposit: null,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 0,
            fundId: "963",
            fundShortText: "אנליסט השתלמות מניות",
            finqRank: 12,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/analyst.png",
            companyShortText: "אנליסט",
          },
          {
            holdingPercentage: null,
            amount: 0,
            fundId: "963",
            fundShortText: "אנליסט השתלמות מניות",
            finqRank: 12,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/analyst.png",
            companyShortText: "אנליסט",
          },
        ],
      },
      {
        productCategoryId: "EDUCATION",
        policyNumberId: "10734588",
        companyId: "511880460",
        fundId: "963",
        fundShortText: "אנליסט השתלמות מניות",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/analyst.png",
        companyShortText: "אנליסט",
        joinDate: "2019-10-31T22:00:00.000Z",
        liquidityDate: "2025-11-29T22:00:00.000Z",
        currentSavings: 4328,
        employerName: "אקשן אייטם סולושנס  בעמ",
        futureRetirementWealthWithDeposits: 0,
        futureRetirementWealthWithoutDeposits: 0,
        status: "ACTIVE",
        posStand: "EMPLOYEE",
        depositManagementFees: null,
        accManagementFees: "0.8",
        employeeProvision: null,
        employerProvision: null,
        compensationProvision: null,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: null,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: null,
        currentWealth: 4328,
        futureAllowanceWithoutDeposit: null,
        futureAllowanceWithDeposit: null,
        monthlyFutureAllowanceWithoutDeposit: null,
        monthlyFutureAllowanceWithDeposit: null,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 0,
            fundId: "963",
            fundShortText: "אנליסט השתלמות מניות",
            finqRank: 12,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/analyst.png",
            companyShortText: "אנליסט",
          },
          {
            holdingPercentage: null,
            amount: 0,
            fundId: "963",
            fundShortText: "אנליסט השתלמות מניות",
            finqRank: 12,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/analyst.png",
            companyShortText: "אנליסט",
          },
        ],
      },
    ],
  },
  {
    type: PensionCategoriesEnum.PENSION,
    data: [
      {
        productCategoryId: "SUPPLEMENTARY",
        policyNumberId: "207992215",
        companyId: "512065202",
        fundId: "13285",
        fundShortText: "מיטב פנסיה כללית עוקב מדד S&P500",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/meitav.png",
        companyShortText: "מיטב",
        joinDate: "2021-12-31T22:00:00.000Z",
        liquidityDate: null,
        currentSavings: 5299,
        employerName: null,
        futureRetirementWealthWithDeposits: null,
        futureRetirementWealthWithoutDeposits: null,
        status: "TERMINATED",
        posStand: "EMPLOYEE",
        depositManagementFees: 1.49,
        accManagementFees: "0.05",
        employeeProvision: 6,
        employerProvision: 6.5,
        compensationProvision: 6,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: null,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: 5299,
        currentWealth: null,
        futureAllowanceWithoutDeposit: 16414,
        futureAllowanceWithDeposit: 24379,
        monthlyFutureAllowanceWithoutDeposit: 79,
        monthlyFutureAllowanceWithDeposit: 118,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 5299.06,
            fundId: "13285",
            fundShortText: "מיטב פנסיה כללית עוקב מדד S&P500",
            finqRank: 3,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/meitav.png",
            companyShortText: "מיטב",
          },
          {
            holdingPercentage: null,
            amount: 5299.06,
            fundId: "13285",
            fundShortText: "מיטב פנסיה כללית עוקב מדד S&P500",
            finqRank: 3,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/meitav.png",
            companyShortText: "מיטב",
          },
        ],
        pensionInsuranceCoverages: [
          {
            monthlyDisabilityAllowance: 17521.45,
            monthlyCost: 92.11,
            salaryRate: 75,
            insuranceCoverageWaiver: 2,
            disabilityExtension: false,
            salaryForDisabilityAndSheerim: 23361.93,
            sheerimWaiverType: "1",
            sheerimWaiverEndDate: "2024-11-30T00:00:00.000+02:00",
            sheerimWidowerAllowance: 0,
            widowerCoverageRate: 0,
            sheerimOrphanAllowance: 0,
            orphanCoverageRate: 0,
            sheerimSupportedParentAllowance: 0,
            supportedParentCoverageRate: 20,
          },
        ],
      },
      {
        productCategoryId: "COMPERHENSIVE",
        policyNumberId: "207992215",
        companyId: "512065202",
        fundId: "1589",
        fundShortText: "מיטב פנסיה מקיפה עוקב מדדי מניות ",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/meitav.png",
        companyShortText: "מיטב",
        joinDate: "2019-07-06T21:00:00.000Z",
        liquidityDate: null,
        currentSavings: 264868,
        employerName: null,
        futureRetirementWealthWithDeposits: null,
        futureRetirementWealthWithoutDeposits: null,
        status: "ACTIVE",
        posStand: "EMPLOYEE",
        depositManagementFees: 1.49,
        accManagementFees: "0.05",
        employeeProvision: 6,
        employerProvision: 6.5,
        compensationProvision: 6,
        salary: 0,
        employeeDeposits: 28948.16,
        employerDeposits: 11712,
        totalYearlyDeposits: 40660.16,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: 264868,
        currentWealth: null,
        futureAllowanceWithoutDeposit: 1177755,
        futureAllowanceWithDeposit: 6819248,
        monthlyFutureAllowanceWithoutDeposit: 5981,
        monthlyFutureAllowanceWithDeposit: 34632,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 75279.6,
            fundId: "1589",
            fundShortText: "מיטב פנסיה מקיפה עוקב מדדי מניות ",
            finqRank: 1,
            finqRiskLevel: 4,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/meitav.png",
            companyShortText: "מיטב",
          },
          {
            holdingPercentage: null,
            amount: 85757.97,
            fundId: "1589",
            fundShortText: "מיטב פנסיה מקיפה עוקב מדדי מניות ",
            finqRank: 1,
            finqRiskLevel: 4,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/meitav.png",
            companyShortText: "מיטב",
          },
        ],
        pensionInsuranceCoverages: [
          {
            monthlyDisabilityAllowance: 17521.45,
            monthlyCost: 92.11,
            salaryRate: 75,
            insuranceCoverageWaiver: 2,
            disabilityExtension: false,
            salaryForDisabilityAndSheerim: 23361.93,
            sheerimWaiverType: "1",
            sheerimWaiverEndDate: "2024-11-30T00:00:00.000+02:00",
            sheerimWidowerAllowance: 0,
            widowerCoverageRate: 0,
            sheerimOrphanAllowance: 0,
            orphanCoverageRate: 0,
            sheerimSupportedParentAllowance: 0,
            supportedParentCoverageRate: 20,
          },
        ],
      },
    ],
  },
  {
    type: PensionCategoriesEnum.INVESTMENT,
    data: [
      {
        productCategoryId: "INVESTMENT",
        policyNumberId: "1053406",
        companyId: "514956465",
        fundId: "7958",
        fundShortText: "מור גמל להשקעה - עוקב מדד S&P500",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/mor.png",
        companyShortText: "מור",
        joinDate: "2024-05-22T21:00:00.000Z",
        liquidityDate: "2024-05-22T21:00:00.000Z",
        currentSavings: 86633,
        employerName: null,
        futureRetirementWealthWithDeposits: 0,
        futureRetirementWealthWithoutDeposits: 0,
        status: "ACTIVE",
        posStand: "SELF_EMPLOYED",
        depositManagementFees: null,
        accManagementFees: "0.8",
        employeeProvision: null,
        employerProvision: null,
        compensationProvision: null,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: 79005,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: null,
        currentWealth: 86633,
        futureAllowanceWithoutDeposit: null,
        futureAllowanceWithDeposit: null,
        monthlyFutureAllowanceWithoutDeposit: null,
        monthlyFutureAllowanceWithDeposit: null,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 86633.46,
            fundId: "7958",
            fundShortText: "מור גמל להשקעה - עוקב מדד S&P500",
            finqRank: 14,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/mor.png",
            companyShortText: "מור",
          },
          {
            holdingPercentage: null,
            amount: 86633.46,
            fundId: "7958",
            fundShortText: "מור גמל להשקעה - עוקב מדד S&P500",
            finqRank: 14,
            finqRiskLevel: 5,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/mor.png",
            companyShortText: "מור",
          },
        ],
      },
    ],
  },
  {
    type: PensionCategoriesEnum.CHILDSAVING,
    data: [
      {
        productCategoryId: "CHILDSAVING",
        policyNumberId: "37605728",
        companyId: "513173393",
        fundId: "11326",
        fundShortText: "אלטשולר שחם חיסכון לילד - חוסכים המעדיפים סיכון בינוני",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/altschuler.png",
        companyShortText: "אלטשולר",
        joinDate: "2017-01-16T22:00:00.000Z",
        liquidityDate: null,
        currentSavings: 0,
        employerName: null,
        futureRetirementWealthWithDeposits: 32334,
        futureRetirementWealthWithoutDeposits: 0,
        status: "TERMINATED",
        posStand: null,
        depositManagementFees: null,
        accManagementFees: "0.23",
        employeeProvision: null,
        employerProvision: null,
        compensationProvision: null,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: null,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: null,
        currentWealth: 0,
        futureAllowanceWithoutDeposit: null,
        futureAllowanceWithDeposit: null,
        monthlyFutureAllowanceWithoutDeposit: null,
        monthlyFutureAllowanceWithDeposit: null,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 0,
            fundId: "11326",
            fundShortText: "אלטשולר שחם חיסכון לילד - חוסכים המעדיפים סיכון בינוני",
            finqRank: 5,
            finqRiskLevel: 3,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/altschuler.png",
            companyShortText: "אלטשולר",
          },
        ],
      },
    ],
  },
  {
    type: PensionCategoriesEnum.PROVIDENT,
    data: [
      {
        productCategoryId: "PROVIDENT",
        policyNumberId: "4702555",
        companyId: "512237744",
        fundId: "9780",
        fundShortText: "מגדל לתגמולים ולפיצויים מסלול לבני 50 עד 60",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
        companyShortText: "מגדל",
        joinDate: "2012-07-31T21:00:00.000Z",
        liquidityDate: null,
        currentSavings: 18697,
        employerName: "ביח סורוקה שרותי בריאות כללית",
        futureRetirementWealthWithDeposits: 84602,
        futureRetirementWealthWithoutDeposits: 30324,
        status: "ACTIVE",
        posStand: "EMPLOYEE",
        depositManagementFees: null,
        accManagementFees: "0.65",
        employeeProvision: null,
        employerProvision: null,
        compensationProvision: null,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: 2291.9,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: 18697,
        currentWealth: 0,
        futureAllowanceWithoutDeposit: 30324,
        futureAllowanceWithDeposit: 84602,
        monthlyFutureAllowanceWithoutDeposit: 0,
        monthlyFutureAllowanceWithDeposit: 0,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 18697.56,
            fundId: "9780",
            fundShortText: "מגדל לתגמולים ולפיצויים מסלול לבני 50 עד 60",
            finqRank: 29,
            finqRiskLevel: 3,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
            companyShortText: "מגדל",
          },
        ],
      },
    ],
  },
  {
    type: PensionCategoriesEnum.MANAGERS,
    data: [
      {
        id: 1697,
        productCategoryId: "MANAGERS",
        policyNumberId: "16025585",
        companyId: "520004896",
        fundId: "17013",
        fundShortText: "מגדל - כללי ",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
        companyShortText: "מגדל",
        joinDate: "2009-12-31T22:00:00.000Z",
        liquidityDate: null,
        currentSavings: 504373,
        employerName: "פינק דיגיטל  בעמ",
        futureRetirementWealthWithDeposits: 2968170,
        futureRetirementWealthWithoutDeposits: 1034235,
        status: "ACTIVE",
        posStand: "EMPLOYEE",
        depositManagementFees: 1.99,
        accManagementFees: "1.1",
        employeeProvision: 106,
        employerProvision: 5.9,
        compensationProvision: 8.33,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: null,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: null,
        currentWealth: 0,
        futureAllowanceWithoutDeposit: null,
        futureAllowanceWithDeposit: null,
        monthlyFutureAllowanceWithoutDeposit: null,
        monthlyFutureAllowanceWithDeposit: null,
        monthsOfDebt: null,
        startOfDebtDate: null,
        debtAmount: null,
        currentSavingsInfo: null,
        informationValidityDate: "2025-01-01",
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 215452.27,
            fundId: "17013",
            fundShortText: "מגדל - כללי ",
            finqRank: 14,
            finqRiskLevel: 3,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
            companyShortText: "מגדל",
          },
          {
            holdingPercentage: null,
            amount: 39574.11,
            fundId: "17013",
            fundShortText: "מגדל - כללי ",
            finqRank: 14,
            finqRiskLevel: 3,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
            companyShortText: "מגדל",
          },
        ],
        pensionInsuranceCoverages: [],
        insuranceCoverages: [
          {
            id: 339,
            policyId: 1697,
            coverageNumId: "520004896130054110130100000000",
            insuranceCoverageName: "מגדלור לחיים 2009 (מנהלים) קופה משלמת לקצבה",
            insuranceCoverageType: "PENSION_SAVINGS",
            policyNumber: null,
            customerId: "300389970",
            insuredType: "PRIMARY",
            insuranceType: "RIDER",
            coverageStartDate: "2009-12-31T22:00:00.000Z",
            coverageEndDate: "2053-12-31T22:00:00.000Z",
            coverageAppendixCode: "1110",
            occupationType: null,
            includesFranchise: null,
            includesNursing: null,
            indexLinkedPension: null,
            developingDisability: null,
            governmentSubsidyCancellation: null,
            paymentEndDate: "2053-12-31T22:00:00.000Z",
            basicInsurancePercentage: 0,
            salaryPercentage: 0,
            insurancePaymentMethod: "ONE_TIME_AMOUNT",
            insuranceAmount: 0,
            ceilingAmount: 0,
            ceilingIncreasePercentage: 0,
            payload: {},
            createdAt: "2025-01-30T08:58:53.688Z",
          },
        ],
      },
      {
        id: 1699,
        productCategoryId: "MANAGERS",
        policyNumberId: "171089587",
        companyId: "520004896",
        fundId: "17013",
        fundShortText: "מגדל - כללי ",
        companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
        companyShortText: "מגדל",
        joinDate: "2012-12-31T22:00:00.000Z",
        liquidityDate: null,
        currentSavings: 1283,
        employerName: "פינק דיגיטל  בעמ",
        futureRetirementWealthWithDeposits: 46,
        futureRetirementWealthWithoutDeposits: 45,
        status: "ACTIVE",
        posStand: "EMPLOYEE",
        depositManagementFees: null,
        accManagementFees: "1.15",
        employeeProvision: 0,
        employerProvision: null,
        compensationProvision: null,
        salary: 0,
        employeeDeposits: null,
        employerDeposits: null,
        totalYearlyDeposits: null,
        debt: false,
        loans: false,
        lien: false,
        bend: false,
        retirementAge: 67,
        currentAllowance: null,
        currentWealth: 0,
        futureAllowanceWithoutDeposit: null,
        futureAllowanceWithDeposit: null,
        monthlyFutureAllowanceWithoutDeposit: null,
        monthlyFutureAllowanceWithDeposit: null,
        monthsOfDebt: null,
        startOfDebtDate: null,
        debtAmount: null,
        currentSavingsInfo: null,
        investmentPlans: [
          {
            holdingPercentage: null,
            amount: 1209.64,
            fundId: "17013",
            fundShortText: "מגדל - כללי ",
            finqRank: 14,
            finqRiskLevel: 3,
            companyLogo: "https://finqfinancedevpublic.blob.core.windows.net/pensiohouselogo/migdal.png",
            companyShortText: "מגדל",
          },
        ],
        pensionInsuranceCoverages: [],
        insuranceCoverages: [
          {
            id: 347,
            policyId: 1699,
            coverageNumId: "520004896130054110130300000000",
            insuranceCoverageName: "מגדלור לחיים 2009 (מנהלים) קופה משלמת לקצבה",
            insuranceCoverageType: "PENSION_SAVINGS",
            policyNumber: null,
            customerId: "300389970",
            insuredType: "PRIMARY",
            insuranceType: "RIDER",
            coverageStartDate: "2012-12-31T22:00:00.000Z",
            coverageEndDate: "2053-12-31T22:00:00.000Z",
            coverageAppendixCode: "1102",
            occupationType: null,
            includesFranchise: null,
            includesNursing: null,
            indexLinkedPension: null,
            developingDisability: null,
            governmentSubsidyCancellation: null,
            paymentEndDate: "2053-12-31T22:00:00.000Z",
            basicInsurancePercentage: 0,
            salaryPercentage: 0,
            insurancePaymentMethod: "ONE_TIME_AMOUNT",
            insuranceAmount: 0,
            ceilingAmount: 0,
            ceilingIncreasePercentage: 0,
            payload: {},
            createdAt: "2025-01-30T08:58:53.688Z",
          },
          {
            id: 344,
            policyId: 1699,
            coverageNumId: "520004896100768300000100000000",
            insuranceCoverageName: "מגדל כושר מש.מורחב  3 חודשי המתנה עם פרנציזה",
            insuranceCoverageType: "WORK_DISABILITY",
            policyNumber: null,
            customerId: "300389970",
            insuredType: "PRIMARY",
            insuranceType: "RIDER",
            coverageStartDate: "2012-12-31T22:00:00.000Z",
            coverageEndDate: "2053-12-31T22:00:00.000Z",
            coverageAppendixCode: "768",
            occupationType: "SPECIFIC",
            includesFranchise: null,
            includesNursing: null,
            indexLinkedPension: null,
            developingDisability: "NONE",
            governmentSubsidyCancellation: null,
            paymentEndDate: "2053-12-31T22:00:00.000Z",
            basicInsurancePercentage: 0,
            salaryPercentage: 0.75,
            insurancePaymentMethod: "ALLOWANCE",
            insuranceAmount: 14674.65,
            ceilingAmount: 17432.19,
            ceilingIncreasePercentage: 20,
            payload: {},
            createdAt: "2025-01-30T08:58:53.688Z",
          },
          {
            id: 345,
            policyId: 1699,
            coverageNumId: "520004896100778300000000000000",
            insuranceCoverageName: "מגדל כושר-שח.מש.מור. עם פרנצ",
            insuranceCoverageType: "RELEASE",
            policyNumber: null,
            customerId: "300389970",
            insuredType: "PRIMARY",
            insuranceType: "RIDER",
            coverageStartDate: "2012-12-31T22:00:00.000Z",
            coverageEndDate: "2053-12-31T22:00:00.000Z",
            coverageAppendixCode: "778",
            occupationType: "SPECIFIC",
            includesFranchise: null,
            includesNursing: null,
            indexLinkedPension: null,
            developingDisability: "NONE",
            governmentSubsidyCancellation: null,
            paymentEndDate: "2053-12-31T22:00:00.000Z",
            basicInsurancePercentage: 0,
            salaryPercentage: 0,
            insurancePaymentMethod: "ALLOWANCE",
            insuranceAmount: 0,
            ceilingAmount: 0,
            ceilingIncreasePercentage: 0,
            payload: {},
            createdAt: "2025-01-30T08:58:53.688Z",
          },
          {
            id: 346,
            policyId: 1699,
            coverageNumId: "520004896100806300000000000000",
            insuranceCoverageName: "עוז - הרחבת הכיסוי לשלוה",
            insuranceCoverageType: "OTHER",
            policyNumber: null,
            customerId: "300389970",
            insuredType: "PRIMARY",
            insuranceType: "RIDER",
            coverageStartDate: "2012-12-31T22:00:00.000Z",
            coverageEndDate: "2053-12-31T22:00:00.000Z",
            coverageAppendixCode: "806",
            occupationType: null,
            includesFranchise: null,
            includesNursing: null,
            indexLinkedPension: null,
            developingDisability: "NONE",
            governmentSubsidyCancellation: null,
            paymentEndDate: "2053-12-31T22:00:00.000Z",
            basicInsurancePercentage: 0,
            salaryPercentage: 0,
            insurancePaymentMethod: "ALLOWANCE",
            insuranceAmount: 14674.65,
            ceilingAmount: 0,
            ceilingIncreasePercentage: 0,
            payload: {},
            createdAt: "2025-01-30T08:58:53.688Z",
          },
        ],
      },
    ],
  },
]

export function useGetMyValuePropositionTableData(
  productType: Ref<string>,
  options: QueryOptions<ValuePropositionTableData> = {}
) {
  const { pensionPortfolioService, additionalParams } = usePensionPortfolioService()
  return useQuery<ValuePropositionTableData>({
    queryKey: ["PensionPortfolioService.getValuePropositionTableData", productType, additionalParams],
    // queryFn: ({ signal }) =>
    //   pensionPortfolioService.value.getValuePropositionTableData({
    //     signal,
    //     params: jsonClean({ productType: productType.value }),
    //   }),
    queryFn: () => {
      return (
        ValuePropositionTableMockData[productType.value] || ValuePropositionTableMockData["pension-savings"]
      )
    },
    staleTime: Infinity,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    retry: false,
    ...options,
  })
}

export type ValuePropositionTableData = {
  pensionSavings?: {
    lumpSum: {
      current: number
      forecast: number
    }
    monthlyAllowance: {
      current: number
      forecast: number
    }
  }

  lifeInsurance?: {
    deathBenefit: {
      monthlyAllowance: number
      lumpSum: number
    }
    survivorsAllowance: {
      monthlyAllowance: number
      lumpSum: number
    }
  }

  disabilityInsurance?: {
    monthlyCost: number
    monthlyAllowance: number
  }
}

// Value Proposition mock data
export const ValuePropositionTableMockData: Record<string, ValuePropositionTableData> = {
  "pension-savings": {
    pensionSavings: {
      lumpSum: {
        current: 242038,
        forecast: 1705038,
      },
      monthlyAllowance: {
        current: 180896,
        forecast: 14975,
      },
    },
  },
  "life-insurance": {
    pensionSavings: {
      lumpSum: {
        current: 242038,
        forecast: 1705038,
      },
      monthlyAllowance: {
        current: 180896,
        forecast: 14975,
      },
    },
    lifeInsurance: {
      deathBenefit: {
        monthlyAllowance: 35,
        lumpSum: 1500000,
      },
      survivorsAllowance: {
        monthlyAllowance: 65,
        lumpSum: 14408,
      },
    },
  },
  "work-disability": {
    pensionSavings: {
      lumpSum: {
        current: 242038,
        forecast: 1705038,
      },
      monthlyAllowance: {
        current: 180896,
        forecast: 14975,
      },
    },
    disabilityInsurance: {
      monthlyCost: 78,
      monthlyAllowance: 12026,
    },
  },
}
